"use client";

import { useState, useRef, useEffect } from "react";
import type { Dispatch } from "react";
import { FUNNEL_CURRENCIES, OBJECTIVE_TYPES, RISK_TOLERANCE_LEVELS, type FunnelWizardState } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { funnelText } from "@/lib/funnel-planner/i18n";
import { createRegionalSettings, REGIONAL_PRESETS } from "@/lib/funnel-planner/regional";
import {
  joinAuditTicketGroups,
  STEP_FOUR_AUDIENCE_FIELDS_SUPPORT_TICKETS,
  STEP_FOUR_CTA_TICKETS,
  STEP_FOUR_DISCLOSURE_PANEL_TICKETS,
  STEP_FOUR_FIELD_INPUT_TICKETS,
  STEP_FOUR_REGIONAL_OBJECTIVE_ASSUMPTIONS_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

// Agency is first by design (Serial Position Effect) — primary user type; do not reorder without data.
// 5 options is intentional — within the non-paralysis range (4–7 per Paradox of Choice research). Do not add more options without evidence that a 6th segment is meaningfully distinct.
const AUDIENCES: Array<{ value: FunnelWizardState["audience"]; labelKey: keyof FunnelPlannerContent; descriptionKey: string; recommended?: boolean }> = [
  { value: "agency",     labelKey: "audienceAgency",     descriptionKey: "audienceAgencyDescription",     recommended: true },
  { value: "franchise",  labelKey: "audienceFranchise",  descriptionKey: "audienceFranchiseDescription" },
  { value: "creator",    labelKey: "audienceCreator",    descriptionKey: "audienceCreatorDescription" },
  { value: "adult",      labelKey: "audienceAdult",      descriptionKey: "audienceAdultDescription" },
  { value: "enterprise", labelKey: "audienceEnterprise", descriptionKey: "audienceEnterpriseDescription" },
];

const GOAL_MAX = 500;
const AUDIENCE_FIELDS_READINESS_TICKET =
  "P1-417001-interactive-step-04-audience-fields-readiness-board";
const AUDIENCE_FIELDS_OFFER_ASSEMBLY_TICKET =
  "P3-417002-interactive-step-04-audience-fields-offer-assembly-configurator";
const REGIONAL_ASSUMPTIONS_READINESS_TICKET =
  "P1-418001-interactive-step-04-regional-objective-assumptions-readiness-board";
const REGIONAL_ASSUMPTIONS_OFFER_ASSEMBLY_TICKET =
  "P3-418002-interactive-step-04-regional-objective-assumptions-offer-assembly-configurator";
const STEP_FOUR_OFFER_ASSEMBLY_CTA_TICKETS = [
  "P1-337001-cta-step-04-button-planner-action-024-offer-assembly-mini-cta-brief",
  "P1-338001-cta-step-04-button-planner-action-025-offer-assembly-mini-cta-brief",
  "P1-339001-cta-step-04-button-planner-action-026-offer-assembly-mini-cta-brief",
  "P1-340001-cta-step-04-button-planner-action-027-offer-assembly-mini-cta-brief",
  "P1-341001-cta-step-04-button-planner-action-028-offer-assembly-mini-cta-brief",
  "P1-342001-cta-step-04-button-planner-action-029-offer-assembly-mini-cta-brief",
  "P1-343001-cta-step-04-button-planner-action-030-offer-assembly-mini-cta-brief",
  "P1-344001-cta-step-04-button-planner-action-031-offer-assembly-mini-cta-brief",
  "P1-345001-cta-step-04-button-planner-action-032-offer-assembly-mini-cta-brief",
  "P1-346001-cta-step-04-button-planner-action-033-offer-assembly-mini-cta-brief",
  "P1-347001-cta-step-04-button-planner-action-034-offer-assembly-mini-cta-brief",
] as const;
const STEP_FOUR_AUDIENCE_ACTION_TICKETS = [
  ...STEP_FOUR_OFFER_ASSEMBLY_CTA_TICKETS,
  ...STEP_FOUR_CTA_TICKETS,
] as const;
const AUDIENCE_FIELDS_AUDIT_TICKETS = joinAuditTicketGroups(
  AUDIENCE_FIELDS_READINESS_TICKET,
  AUDIENCE_FIELDS_OFFER_ASSEMBLY_TICKET,
  STEP_FOUR_AUDIENCE_FIELDS_SUPPORT_TICKETS,
);
const REGIONAL_ASSUMPTIONS_AUDIT_TICKETS = joinAuditTicketGroups(
  REGIONAL_ASSUMPTIONS_READINESS_TICKET,
  REGIONAL_ASSUMPTIONS_OFFER_ASSEMBLY_TICKET,
  STEP_FOUR_FIELD_INPUT_TICKETS,
  STEP_FOUR_DISCLOSURE_PANEL_TICKETS,
  STEP_FOUR_REGIONAL_OBJECTIVE_ASSUMPTIONS_SUPPORT_TICKETS,
);
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";

const TEMPLATE_AUDIENCE_MAP: Record<string, FunnelWizardState["audience"]> = {
  "agency-white-label":        "agency",
  "franchise-multi-location":  "franchise",
  "ai-search-geo":             "enterprise",
  "adult-compliance":          "adult",
  "b2b-enterprise":            "enterprise",
  "ecommerce-dtc":             "creator",
  "high-ticket-coaching":      "creator",
  "saas-plg":                  "enterprise",
};

function offerAssemblyCtaTicketsForAudience(actionIndex: number): string {
  return STEP_FOUR_AUDIENCE_ACTION_TICKETS
    .filter((_, ticketIndex) => ticketIndex % AUDIENCES.length === actionIndex)
    .join(";");
}

function audienceOfferAssemblyCtaAttributes(ticketGroup: string, selected: boolean) {
  return {
    "data-funnel-planner-offer-assembly-cta": "true",
    "data-funnel-planner-step-four-offer-assembly-cta": "true",
    "data-audit-ticket": ticketGroup,
    "data-tier-one-family": "offer_assembly_mini_cta_brief",
    "data-gamification-current-state": selected ? "complete_result" : "active_inprogress",
    "data-gamification-reduced-motion-static": "supported",
    "data-gamification-supported-states": READINESS_SUPPORTED_STATES,
    "data-readiness-ready-count": selected ? 1 : 0,
    "data-readiness-total-count": 1,
    "data-readiness-tier": selected ? "brief_piece_ready" : "brief_piece_available",
    "data-blockers-bucket": selected ? "none" : "one",
  } as const;
}

type Action =
  | { type: "SET_AUDIENCE"; audience: FunnelWizardState["audience"]; source?: "automatic" }
  | { type: "SET_GOAL"; goal: string }
  | { type: "SET_REGIONAL_SETTINGS"; settings: FunnelWizardState["regionalSettings"] }
  | { type: "SET_AUDIENCE_GOAL_DETAILS"; details: FunnelWizardState["audienceGoalDetails"] };

const PRO_INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  borderRadius: 6,
  border: "1px solid #334155",
  background: "rgba(255,255,255,0.05)",
  padding: "8px 10px",
  fontSize: 13,
  color: "#e2e8f0",
  outline: "none",
  boxSizing: "border-box",
};

const PRO_LABEL_STYLE: React.CSSProperties = {
  display: "block",
  marginBottom: 5,
  color: "#94a3b8",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

export function AudienceGoalStep({
  state,
  dispatch,
  content,
}: {
  state: FunnelWizardState;
  dispatch: Dispatch<Action>;
  content: FunnelPlannerContent;
}) {
  const [hoveredCard, setHoveredCard] = useState<FunnelWizardState["audience"] | null>(null);
  const [focusedAudience, setFocusedAudience] = useState<FunnelWizardState["audience"] | null>(null);
  const [goalTouched, setGoalTouched] = useState(false);
  const [goalHovered, setGoalHovered] = useState(false);
  const [goalFocused, setGoalFocused] = useState(false);
  const [nextReady, setNextReady] = useState(false);

  const fieldsetRef = useRef<HTMLFieldSetElement>(null);
  const goalRef = useRef<HTMLTextAreaElement>(null);
  const prevCanGoNext = useRef(false);

  const goalError = goalTouched && state.goal.trim().length === 0;
  const canGoNext = !!state.audience && state.goal.trim().length > 0;
  const audienceReadyCount = Number(Boolean(state.audience)) + Number(state.goal.trim().length > 0);
  const assumptionReadyCount = [
    state.regionalSettings.region,
    state.regionalSettings.currency,
    state.audienceGoalDetails.objectiveType,
    state.audienceGoalDetails.objectiveValue,
    state.audienceGoalDetails.timeframeMonths,
    state.audienceGoalDetails.budgetCeiling,
    state.audienceGoalDetails.riskTolerance,
    state.audienceGoalDetails.buyingTrigger,
  ].filter((value) => value !== undefined && value !== null && value !== "").length;

  const goalPlaceholderKey = state.templateId
    ? (`templates.${state.templateId}.goalPlaceholder` as keyof FunnelPlannerContent)
    : null;

  useEffect(() => {
    if (!state.audience && state.templateId) {
      const derived = TEMPLATE_AUDIENCE_MAP[state.templateId];
      if (derived) {
        // Users can freely change audience selection at any time — this is intentional per User Control and Freedom (Nielsen). Do not add confirmation dialogs or lock selection.
        dispatch({ type: "SET_AUDIENCE", audience: derived, source: "automatic" });
      }
    }
  }, [state.audience, state.templateId, dispatch]);

  useEffect(() => {
    if (!prevCanGoNext.current && canGoNext) {
      setNextReady(true);
      const t = setTimeout(() => setNextReady(false), 1400);
      return () => clearTimeout(t);
    }
    prevCanGoNext.current = canGoNext;
  }, [canGoNext]);

  const completedSteps = [
    state.templateId ? funnelText(content, "stepNameTemplate", "Template") : null,
    (state.trafficChannels && state.trafficChannels.length > 0) ? funnelText(content, "stepNameTraffic", "Traffic") : null,
    (state.products && state.products.length > 0) ? funnelText(content, "stepNameProducts", "Products") : null,
  ].filter(Boolean);

  const updateAudienceGoalDetails = (patch: Partial<FunnelWizardState["audienceGoalDetails"]>) => {
    dispatch({
      type: "SET_AUDIENCE_GOAL_DETAILS",
      details: {
        ...state.audienceGoalDetails,
        ...patch,
      },
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {completedSteps.length > 0 && (
        <div
          aria-label={funnelText(content, "audienceCompletedStepsAriaLabel", "Completed steps")}
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            padding: "8px 12px",
            borderRadius: 6,
            background: "rgba(34,197,94,0.06)",
            border: "1px solid rgba(34,197,94,0.18)",
          }}
        >
          {(completedSteps as string[]).map((step) => (
            <span
              key={step}
              style={{ fontSize: 12, color: "#22c55e", display: "flex", alignItems: "center", gap: 4 }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 6l3 3 5-5" stroke="#22c55e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {step}
            </span>
          ))}
        </div>
      )}

      {!state.audience && !state.goal && (
        <p
          role="status"
          style={{ fontSize: 13, color: "#94a3b8", margin: 0, padding: "2px 0" }}
        >
          {funnelText(content, "audienceInitialPrompt", "Choose your audience type and describe your goal to unlock your personalized funnel plan.")}
        </p>
      )}

      <fieldset
        ref={fieldsetRef}
        aria-required="true"
        aria-invalid={state.audience === null || undefined}
        tabIndex={-1}
        data-funnel-planner-audience-fields="true"
        data-audit-ticket={AUDIENCE_FIELDS_AUDIT_TICKETS}
        data-tier-one-family="readiness_board"
        data-gamification-current-state={audienceReadyCount >= 2 ? "complete_result" : audienceReadyCount > 0 ? "partial" : "active_inprogress"}
        data-gamification-reduced-motion-static="supported"
        data-gamification-supported-states={READINESS_SUPPORTED_STATES}
        data-readiness-ready-count={audienceReadyCount}
        data-readiness-total-count={2}
        data-readiness-tier={audienceReadyCount >= 2 ? "audience_goal_ready" : "audience_goal_needed"}
        data-blockers-bucket={audienceReadyCount >= 2 ? "none" : "one_two"}
        style={{ border: "none", margin: 0, padding: 0 }}
      >
        <span className="sr-only" role="status" aria-live="polite">
          {audienceReadyCount >= 2
            ? funnelText(content, "gamificationAudienceFieldsReady", "Audience and goal captured")
            : funnelText(content, "gamificationAudienceFieldsWaiting", "Audience and goal fields waiting")}
        </span>
        <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: "#e2e8f0" }}>
          {funnelText(content, "audienceLegend", "Who is this funnel for? Select the audience type that best matches your client or project.")}
          <span className="visually-hidden">
            {" "}
            {funnelText(content, "audienceLegendRequiredHint", "(required, {count} options)", { count: AUDIENCES.length })}
          </span>
        </legend>
        <div
          role="radiogroup"
          aria-label={funnelText(content, "audienceLegend", "Who is this funnel for? Select the audience type that best matches your client or project.")}
          style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(2, 1fr)" }}
        >
          {AUDIENCES.map(({ value, labelKey, descriptionKey, recommended }, idx) => {
            const selected = state.audience === value;
            const hovered = hoveredCard === value;
            const focused = focusedAudience === value;
            const auditTicket = offerAssemblyCtaTicketsForAudience(idx);
            let borderColor = "#1e293b";
            if (selected) borderColor = "#2563eb";
            else if (focused) borderColor = "#60a5fa";
            else if (hovered) borderColor = "#334155";
            let bgColor = "rgba(255,255,255,0.03)";
            if (selected) bgColor = "rgba(37,99,235,0.10)";
            else if (hovered) bgColor = "rgba(255,255,255,0.06)";
            return (
              <label
                key={value}
                className="audience-card"
                data-funnel-planner-audience-card="true"
                onMouseEnter={() => setHoveredCard(value)}
                onMouseLeave={() => setHoveredCard(null)}
                {...audienceOfferAssemblyCtaAttributes(auditTicket, selected)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                  borderRadius: 8,
                  border: `2px solid ${borderColor}`,
                  background: bgColor,
                  padding: "12px 14px",
                  cursor: "pointer",
                  textAlign: "start",
                  userSelect: "none",
                  minHeight: 44,
                  boxSizing: "border-box",
                  position: "relative",
                  boxShadow: focused ? "0 0 0 3px rgba(96,165,250,0.28)" : "none",
                }}
              >
                <input
                  type="radio"
                  name="audience"
                  value={value ?? ""}
                  checked={selected}
                  autoFocus={idx === 0 && !state.audience}
                  aria-label={content[labelKey] as string}
                  className="sr-only"
                  onFocus={() => setFocusedAudience(value)}
                  onBlur={() => setFocusedAudience(null)}
                  onChange={() => dispatch({ type: "SET_AUDIENCE", audience: value })}
                />
                {recommended && (
                  <span
                    data-funnel-planner-recommended-badge="true"
                    style={{
                      position: "absolute",
                      top: 6,
                      insetInlineEnd: 8,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      color: "#2563eb",
                      background: "rgba(37,99,235,0.12)",
                      borderRadius: 4,
                      padding: "1px 6px",
                    }}
                  >
                    {funnelText(content, "audienceRecommendedBadge", "Recommended")}
                  </span>
                )}
                {selected && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#2563eb",
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: selected ? "#93c5fd" : "#e2e8f0",
                    paddingLeft: selected ? 16 : 0,
                  }}
                >
                  {content[labelKey] as string}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    paddingLeft: selected ? 16 : 0,
                  }}
                >
                  {funnelText(content, descriptionKey, "")}
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 4,
                    minHeight: 20,
                    borderRadius: 999,
                    border: selected ? "1px solid rgba(16,185,129,0.45)" : "1px solid rgba(96,165,250,0.35)",
                    background: selected ? "rgba(16,185,129,0.12)" : "rgba(37,99,235,0.10)",
                    color: selected ? "#a7f3d0" : "#bfdbfe",
                    fontSize: 9,
                    fontWeight: 800,
                    padding: "1px 6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selected
                    ? content.gamificationOfferAssemblyCtaSelected as string
                    : content.gamificationOfferAssemblyCtaScopeThis as string}
                </span>
                <span className="sr-only" role="status" aria-live="polite">
                  {selected
                    ? content.gamificationOfferAssemblyCtaSelected as string
                    : content.gamificationOfferAssemblyCtaEmptyStatus as string}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div>
        <label
          htmlFor="goal"
          style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6, color: "#e2e8f0" }}
        >
          {content.goalLabel}
          <span aria-hidden="true" style={{ color: "#f59e0b", marginInlineStart: 4 }}>*</span>
          <span className="sr-only"> {content.goalRequired as string}</span>
        </label>
        <textarea
          id="goal"
          ref={goalRef}
          required
          aria-required="true"
          aria-invalid={goalError || undefined}
          aria-describedby={goalError ? "goal-helper goal-counter goal-error" : "goal-helper goal-counter"}
          value={state.goal}
          onChange={(e) => dispatch({ type: "SET_GOAL", goal: e.target.value.slice(0, GOAL_MAX) })}
          onMouseEnter={() => setGoalHovered(true)}
          onMouseLeave={() => setGoalHovered(false)}
          onFocus={() => setGoalFocused(true)}
          onBlur={() => { setGoalTouched(true); setGoalFocused(false); }}
          rows={3}
          maxLength={GOAL_MAX}
          placeholder={
            goalPlaceholderKey && content[goalPlaceholderKey]
              ? (content[goalPlaceholderKey] as string)
              : funnelText(content, "goalFallbackPlaceholder", "Grow organic B2B SaaS trial signups from 150 to 400/month by Q4 via SEO + LinkedIn")
          }
          style={{
            width: "100%",
            height: 84,
            borderRadius: 6,
            border: goalError
              ? "1px solid #ef4444"
              : goalFocused
              ? "1px solid #3b82f6"
              : goalHovered
              ? "1px solid #475569"
              : "1px solid #334155",
            background: "transparent",
            padding: "8px 12px",
            fontSize: 13,
            lineHeight: "20px",
            color: "var(--foreground, #e2e8f0)",
            resize: "none",
            boxSizing: "border-box",
            outline: goalFocused ? "2px solid #2563eb" : "none",
            outlineOffset: 2,
          }}
        />
        <p
          id="goal-helper"
          style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}
        >
          {funnelText(content, "goalHelper", "Your goal shapes your funnel structure, traffic strategy, and projections.")}
        </p>
        {goalError && (
          <p
            id="goal-error"
            role="alert"
            style={{ marginTop: 4, fontSize: 12, color: "#ef4444" }}
          >
            {content.goalRequired as string}
          </p>
        )}
        {/* textAlign: 'right' is intentional — matches Twitter/LinkedIn/Slack conventions (Jakob's Law). Do not change to left-aligned. */}
        <p
          id="goal-counter"
          aria-live="polite"
          aria-atomic="true"
          style={{ marginTop: 4, textAlign: "right", fontSize: 12, color: "#475569" }}
        >
          {(content.goalCharCounter as string)
            ?.replace("{count}", String(state.goal.length))
            .replace("{max}", String(GOAL_MAX))}
        </p>
      </div>

      <details
        data-funnel-planner-regional-objective-assumptions="true"
        data-audit-ticket={REGIONAL_ASSUMPTIONS_AUDIT_TICKETS}
        data-tier-one-family="readiness_board"
        data-gamification-current-state={assumptionReadyCount >= 4 ? "complete_result" : assumptionReadyCount > 2 ? "partial" : "active_inprogress"}
        data-gamification-reduced-motion-static="supported"
        data-gamification-supported-states={READINESS_SUPPORTED_STATES}
        data-readiness-ready-count={assumptionReadyCount}
        data-readiness-total-count={8}
        data-readiness-tier={assumptionReadyCount >= 4 ? "assumptions_review_ready" : "assumptions_draft"}
        data-blockers-bucket={assumptionReadyCount >= 4 ? "none" : "three_four"}
        style={{
          borderRadius: 8,
          border: "1px solid #1e293b",
          background: "rgba(255,255,255,0.03)",
          padding: "12px 14px",
        }}
      >
        <span className="sr-only" role="status" aria-live="polite">
          {assumptionReadyCount >= 4
            ? funnelText(content, "gamificationRegionalAssumptionsReady", "Regional assumptions ready for review")
            : funnelText(content, "gamificationRegionalAssumptionsWaiting", "Regional assumptions waiting for planning inputs")}
        </span>
        <summary
          style={{
            cursor: "pointer",
            color: "#cbd5e1",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {funnelText(content, "audienceAdvancedPlanningTitle", "Regional and objective assumptions")}
        </summary>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 14 }}>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceRegionLabel", "Region")}</span>
            <select
              value={state.regionalSettings.region}
              onChange={(event) => dispatch({ type: "SET_REGIONAL_SETTINGS", settings: createRegionalSettings(event.target.value as FunnelWizardState["regionalSettings"]["region"]) })}
              style={PRO_INPUT_STYLE}
            >
              {REGIONAL_PRESETS.map((preset) => (
                <option key={preset.region} value={preset.region}>{preset.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceCurrencyLabel", "Currency")}</span>
            <select
              value={state.regionalSettings.currency}
              onChange={(event) => dispatch({
                type: "SET_REGIONAL_SETTINGS",
                settings: {
                  ...state.regionalSettings,
                  currency: event.target.value as FunnelWizardState["regionalSettings"]["currency"],
                  assumptionsLabel: "user_input",
                },
              })}
              style={PRO_INPUT_STYLE}
            >
              {FUNNEL_CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceObjectiveTypeLabel", "Objective type")}</span>
            <select
              value={state.audienceGoalDetails.objectiveType ?? ""}
              onChange={(event) => updateAudienceGoalDetails({
                objectiveType: event.target.value
                  ? event.target.value as FunnelWizardState["audienceGoalDetails"]["objectiveType"]
                  : undefined,
              })}
              style={PRO_INPUT_STYLE}
            >
              <option value="">{funnelText(content, "audienceObjectiveNoneOption", "Use projection default")}</option>
              {OBJECTIVE_TYPES.map((type) => (
                <option key={type} value={type}>{type.replace(/_/g, " ")}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceObjectiveValueLabel", "Objective value")}</span>
            <input
              type="number"
              min={0}
              value={state.audienceGoalDetails.objectiveValue ?? ""}
              onChange={(event) => updateAudienceGoalDetails({
                objectiveValue: event.target.value === "" ? undefined : Math.max(0, Number(event.target.value)),
              })}
              style={PRO_INPUT_STYLE}
            />
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceTimeframeLabel", "Timeframe months")}</span>
            <input
              type="number"
              min={1}
              max={60}
              value={state.audienceGoalDetails.timeframeMonths ?? ""}
              onChange={(event) => updateAudienceGoalDetails({
                timeframeMonths: event.target.value === "" ? undefined : Math.max(1, Math.min(60, Number(event.target.value))),
              })}
              style={PRO_INPUT_STYLE}
            />
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceBudgetCeilingLabel", "Budget ceiling")}</span>
            <input
              type="number"
              min={0}
              value={state.audienceGoalDetails.budgetCeiling ?? ""}
              onChange={(event) => updateAudienceGoalDetails({
                budgetCeiling: event.target.value === "" ? undefined : Math.max(0, Number(event.target.value)),
              })}
              style={PRO_INPUT_STYLE}
            />
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceRiskToleranceLabel", "Risk tolerance")}</span>
            <select
              value={state.audienceGoalDetails.riskTolerance ?? ""}
              onChange={(event) => updateAudienceGoalDetails({
                riskTolerance: event.target.value
                  ? event.target.value as FunnelWizardState["audienceGoalDetails"]["riskTolerance"]
                  : undefined,
              })}
              style={PRO_INPUT_STYLE}
            >
              <option value="">{funnelText(content, "audienceRiskDefaultOption", "Not set")}</option>
              {RISK_TOLERANCE_LEVELS.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={PRO_LABEL_STYLE}>{funnelText(content, "audienceBuyingTriggerLabel", "Buying trigger")}</span>
            <input
              type="text"
              maxLength={220}
              value={state.audienceGoalDetails.buyingTrigger ?? ""}
              onChange={(event) => updateAudienceGoalDetails({ buyingTrigger: event.target.value || undefined })}
              style={PRO_INPUT_STYLE}
            />
          </label>
        </div>
      </details>

      {!canGoNext && (state.goal.length > 0 || !!state.audience) && (
        <p
          role="status"
          aria-live="polite"
          style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}
        >
          {!state.audience && !state.goal.trim()
            ? funnelText(content, "audienceGoalMissing", "Select an audience and enter your goal to continue.")
            : !state.audience
            ? funnelText(content, "audienceMissing", "Select an audience to continue.")
            : funnelText(content, "goalMissing", "Enter your goal to continue.")}
        </p>
      )}

      {nextReady && (
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ fontSize: 12, color: "#22c55e", margin: 0 }}
        >
          {funnelText(content, "readyToContinue", "Ready to continue!")}
        </p>
      )}

    </div>
  );
}
